import { useCallback, useEffect, useMemo, useRef } from 'react';
import useSWRInfinite from 'swr/infinite';
import _ from 'lodash';
import { PaginatedResponse } from '@/types/responses';
import { usePagesStateStore } from '@/stores';
import { EventsDataResult } from './eventsDataShared';

interface UseInfiniteListParams<T> {
    cacheKey: string;
    pageStateKey?: string;
    fetchPage: (pageIndex: number) => Promise<PaginatedResponse<T>>;
    getId?: (item: T) => string | number;
    revalidateFirstPage?: boolean;
    refreshInterval?: number;
}

export interface InfiniteListResult<T> extends EventsDataResult<T> {
    fetched: T[];
}

export function useInfiniteList<T>({
    cacheKey,
    pageStateKey,
    fetchPage,
    getId,
    revalidateFirstPage = false,
    refreshInterval = 0,
}: UseInfiniteListParams<T>): InfiniteListResult<T> {
    const { getPageState, setPageState } = usePagesStateStore();

    const getKey = useCallback(
        (pageIndex: number, previousPageData: PaginatedResponse<T> | null) => {
            if (previousPageData && !previousPageData.items.length) return null;
            return [cacheKey, pageIndex];
        },
        [cacheKey],
    );

    const { data, error, size, setSize, isLoading, isValidating, mutate } =
        useSWRInfinite(
            getKey,
            ([, pageIndex]) => fetchPage(pageIndex as number),
            {
                revalidateFirstPage,
                revalidateOnFocus: false,
                revalidateIfStale: false,
                revalidateOnMount: true,
                dedupingInterval: 0,
                refreshInterval,
            },
        );

    const previousCacheKey = useRef(cacheKey);
    useEffect(() => {
        if (previousCacheKey.current !== cacheKey) {
            previousCacheKey.current = cacheKey;
            setSize(1);
        }
    }, [cacheKey, setSize]);

    const initialSizeRestored = useRef(false);
    const restoredPageStateKey = useRef<string | undefined>(undefined);
    useEffect(() => {
        if (!pageStateKey) return;
        if (restoredPageStateKey.current !== pageStateKey) {
            restoredPageStateKey.current = pageStateKey;
            initialSizeRestored.current = false;
        }
        if (initialSizeRestored.current) return;
        const { size: storedSize } = getPageState(pageStateKey);
        if (storedSize > 1) {
            setSize(storedSize);
        }
        initialSizeRestored.current = true;
    }, [pageStateKey, getPageState, setSize]);

    useEffect(() => {
        if (pageStateKey && size >= 1) {
            const current = getPageState(pageStateKey);
            setPageState(pageStateKey, { ...current, size });
        }
    }, [pageStateKey, size, getPageState, setPageState]);

    const fetched = useMemo(
        () => (data ? data.flatMap((page) => page.items) : []),
        [data],
    );

    const events = useMemo(
        () => (getId ? _.uniqBy(fetched, getId) : fetched),
        [fetched, getId],
    );

    const hasMore = useMemo(() => {
        if (!data || data.length === 0) return false;
        const lastPage = data[data.length - 1];
        return lastPage.page.page < lastPage.page.totalPages;
    }, [data]);

    const totalItems = useMemo(() => {
        if (!data || data.length === 0) return 0;
        return data[data.length - 1].page.totalItems;
    }, [data]);

    const isLoadingMore = isValidating && size > 1;

    const loadMore = useCallback(() => {
        if (!isValidating && hasMore) {
            void setSize(size + 1);
        }
    }, [isValidating, hasMore, setSize, size]);

    return {
        events,
        fetched,
        totalItems,
        pageStateKey,
        error,
        isLoading,
        isLoadingMore,
        hasMore,
        loadMore,
        mutate,
    };
}
